import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';

import { isUsedOnMobile, joinClassNames, SCREEN_TYPE } from '../../../util/util';
import { LiveHelper } from '../../../util/live-helper';
import * as QPlayerSVGElement from '../../QPlayerSVGElement';
import ControlButton from '../ControlButton';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import { usePlayer } from '../../../contexts/PlayerContext';

import './CenterControls.scss';

interface CenterControlsProps {
	settings?: any;
	playerHover?: boolean;
	isMobile?: boolean;
	isInlinePlayer?: boolean;
}

const CenterControls = ({ settings, playerHover, isMobile, isInlinePlayer }: CenterControlsProps) => {
	const { t } = useTranslation();
	const { currentPosition: pos, duration, isPlaying } = usePlayer();
	const { isMediaLive, currentMediaId, playlistId, screenType, configParams } = useConfiguration();
	const currentPosition = typeof pos === 'number' ? pos : 0;

	const color = settings?.playerStyling?.primaryColor ?? '#126cfc';
	const opacity = Number(settings?.playerStyling?.primaryOpacity ?? 100);
	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
	const hoverStyle = isMobile ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };

	const currentPlayerId = `player-${playlistId || currentMediaId}-${configParams.containerKey}`;

	const isAtLive = useMemo(() => LiveHelper.isAtLive(isMediaLive, currentPosition, duration), [currentPosition, duration, isMediaLive]);

	const goToLive = useCallback(() => {
		if (isAtLive) {
			return;
		}
		(window as any).GoBrain.widgets(currentPlayerId)?.position?.(LiveHelper.LIVE_POSITION_OFFSET);
	}, [currentPlayerId, isAtLive]);

	const handleButtonRewind = useCallback(() => {
		let rewindPosition = currentPosition - 15;
		if (rewindPosition < 0) {
			rewindPosition = 0;
		}
		(window as any).GoBrain.widgets(currentPlayerId)?.position?.(rewindPosition);
	}, [currentPlayerId, currentPosition]);

	const handleButtonForward = useCallback(() => {
		let forwardPosition = currentPosition + 15;
		if (isMediaLive && forwardPosition >= duration) {
			goToLive();
			return;
		}

		(window as any).GoBrain.widgets(currentPlayerId)?.position?.(forwardPosition);
	}, [currentPlayerId, currentPosition, duration, goToLive, isMediaLive]);

	const handleButtonPlay = useCallback(() => {
		(window as any).GoBrain.widgets(currentPlayerId)?.play?.();
	}, [currentPlayerId]);

	const handleButtonPause = useCallback(() => {
		(window as any).GoBrain.widgets(currentPlayerId)?.pause?.();
	}, [currentPlayerId]);

	const solidColor = settings?.bottomControls?.solidBackgroundBottomColor ?? '#000000';
	const solidColorStyle = settings.bottomControls.showSolidBackgroundBottomControl
		? {
			backgroundColor: tinycolor({
				...tinycolor(solidColor).toRgb(),
				a: Number(settings.bottomControls.solidBackgroundBottomControlOpacity ?? 100) / 100,
			}).toRgbString(),
		}
		: undefined;

	let showCenterControls;

	if (screenType === SCREEN_TYPE.mobile && !isUsedOnMobile(configParams)) {
		showCenterControls = true;
	} else {
		showCenterControls =
			(isInlinePlayer || configParams?.qplayerPreview === "true") &&
			settings?.bottomControls?.playPausePosition === 'center' &&
			(screenType === SCREEN_TYPE.mobile || screenType === SCREEN_TYPE.small || isMobile);
	}

	if (!showCenterControls) {
		return null;
	}

	return (
		<div className={joinClassNames('controlsOverlayCenterControls', playerHover ? 'controlsOverlayCenterControls--visible' : undefined)}>
			{settings.bottomControls.showRewindForwardButtons &&
				(LiveHelper.getStreamType(isMediaLive, duration) === LiveHelper.LIVE_STREAM_TYPE_DVR ||
					LiveHelper.getStreamType(isMediaLive, duration) === LiveHelper.LIVE_STREAM_TYPE_ON_DEMAND) && (
					<ControlButton
						tabIndex={0}
						ariaLabel={t('REWIND')}
						className="bottomControlbarButtonRewind"
						hoverStyle={hoverStyle}
						onClick={handleButtonRewind}
						tooltip={t('REWIND')}
						icon={<QPlayerSVGElement.Rewind />}
						style={solidColorStyle}
					/>
				)}

			<ControlButton
				tabIndex={0}
				ariaLabel={isPlaying ? t('PAUSE') : t('PLAY')}
				className="controlsOverlayCenterControls__play-btn"
				hoverStyle={hoverStyle}
				onClick={isPlaying ? handleButtonPause : handleButtonPlay}
				tooltip={isPlaying ? t('PAUSE') : t('PLAY')}
				icon={isPlaying ? <QPlayerSVGElement.Pause /> : <QPlayerSVGElement.Play />}
				style={solidColorStyle}
			/>

			{settings.bottomControls.showRewindForwardButtons &&
				(LiveHelper.getStreamType(isMediaLive, duration) === LiveHelper.LIVE_STREAM_TYPE_DVR ||
					LiveHelper.getStreamType(isMediaLive, duration) === LiveHelper.LIVE_STREAM_TYPE_ON_DEMAND) && (
					<ControlButton
						tabIndex={0}
						ariaLabel={t('FORWARD')}
						hoverStyle={hoverStyle}
						onClick={isAtLive ? undefined : handleButtonForward}
						tooltip={t('FORWARD')}
						icon={<QPlayerSVGElement.Forward />}
						style={solidColorStyle}
					/>
				)}
		</div>
	);
};

export default CenterControls;
